// ═══════════════════════════════════════════════════════════════════════
// verticals.ts — operator-door vertical taxonomy
// ═══════════════════════════════════════════════════════════════════════
// Each operator door (<slug>.guild.sloelabs.com) is bound to ONE vertical.
// VERTICALS maps a vertical key → its display label + the category chips shown
// on the Studio landing for that door. Each chip value doubles as the
// demo.category filter key (Explore.tsx), so every sub-category here needs
// matching DemoData objects in demos.ts to render cards. "Featured" and "All"
// are sentinels handled specially by the Explore filter.
// ═══════════════════════════════════════════════════════════════════════

export interface Vertical {
  label: string;
  categories: string[];
  hero?: { headline: string; subcopy: string };
}

export const VERTICALS = {
  sports: {
    label: "Sports",
    categories: ["Featured", "All", "Football", "Rugby", "Tennis", "Cricket", "Basketball", "Athletics", "Combat Sports", "Esports"],
    hero: {
      headline: "Turn your sports data, scouting, and performance tracking into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with athlete screens, scouting agents, predictive Decisions, automated Receipts, and a career upgrade path shaped around your role.",
    },
  },
  "real-estate": {
    label: "Real Estate",
    categories: ["Featured", "All", "Residential Sales", "Property Management", "Commercial", "Construction", "Leasing", "Development", "Off-Plan"],
    hero: {
      headline: "Turn your listings, deals, and compliance into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with property and pipeline screens, deal-matching agents, compliance Decisions, audit-ready Receipts, and a growth path shaped around your desk.",
    },
  },
  "oil-gas": {
    label: "Oil & Gas",
    categories: ["Featured", "All", "Upstream", "Midstream", "Downstream", "Oilfield Services", "Drilling", "Renewables", "Utilities"],
    hero: {
      headline: "Turn your assets, inspections, and HSE evidence into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with asset and maintenance screens, risk-monitoring agents, permit Decisions, inspection Receipts, and an operations upgrade path shaped around your site.",
    },
  },
  healthcare: {
    label: "Healthcare",
    categories: ["Featured", "All", "Clinics", "Telehealth", "Dental", "Mental Health", "Medical Devices", "Pharmacy", "Home Care"],
    hero: {
      headline: "Turn your patients, scheduling, and clinical records into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with patient and care screens, triage agents, treatment Decisions, compliance Receipts, and a practice upgrade path shaped around your role — privacy-first by design.",
    },
  },
  finance: {
    label: "Finance",
    categories: ["Featured", "All", "Banking", "Insurance", "Wealth Management", "Lending", "Accounting", "Fintech", "Payments"],
    hero: {
      headline: "Turn your clients, portfolios, and reporting into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with account and portfolio screens, monitoring agents, risk Decisions, audit-ready Receipts, and a growth path shaped around your book.",
    },
  },
  legal: {
    label: "Legal",
    categories: ["Featured", "All", "Corporate", "Litigation", "Immigration", "IP", "Real Estate Law", "Family", "Compliance"],
    hero: {
      headline: "Turn your matters, contracts, and casework into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with matter and document screens, review agents, conflict Decisions, billable Receipts, and a practice upgrade path shaped around your role.",
    },
  },
  "marketing-media": {
    label: "Marketing & Media",
    categories: ["Featured", "All", "Agencies", "Content", "Advertising", "PR", "Social", "Production", "Influencer"],
    hero: {
      headline: "Turn your campaigns, content, and clients into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with campaign and content screens, performance agents, spend Decisions, reporting Receipts, and a growth path shaped around your accounts.",
    },
  },
  "retail-ecommerce": {
    label: "Retail & Ecommerce",
    categories: ["Featured", "All", "D2C Brands", "Marketplaces", "Omnichannel", "Fashion", "Subscription", "Wholesale"],
    hero: {
      headline: "Turn your catalog, orders, and customers into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with product and order screens, inventory agents, pricing Decisions, fulfillment Receipts, and a growth path shaped around your store.",
    },
  },
  hospitality: {
    label: "Hospitality",
    categories: ["Featured", "All", "Restaurants", "Hotels", "Events", "Catering", "Travel", "Tourism"],
    hero: {
      headline: "Turn your bookings, guests, and operations into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with reservation and service screens, guest agents, staffing Decisions, service Receipts, and an upgrade path shaped around your venue.",
    },
  },
  logistics: {
    label: "Logistics",
    categories: ["Featured", "All", "Freight", "Last-Mile", "Warehousing", "Fleet", "Supply Chain", "Customs"],
    hero: {
      headline: "Turn your shipments, fleet, and customs into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with shipment and route screens, tracking agents, routing Decisions, delivery Receipts, and an operations upgrade path shaped around your network.",
    },
  },
  manufacturing: {
    label: "Manufacturing",
    categories: ["Featured", "All", "Industrial", "Automotive", "Electronics", "Food Production", "Textiles", "Machinery"],
    hero: {
      headline: "Turn your production, quality, and supply chain into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with line and inventory screens, quality agents, scheduling Decisions, compliance Receipts, and an efficiency upgrade path shaped around your floor.",
    },
  },
  construction: {
    label: "Construction",
    categories: ["Featured", "All", "Residential Build", "Commercial Build", "Civil", "Trades", "Project Management", "Architecture"],
    hero: {
      headline: "Turn your projects, crews, and compliance into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with project and site screens, scheduling agents, safety Decisions, inspection Receipts, and a delivery upgrade path shaped around your builds.",
    },
  },
  education: {
    label: "Education",
    categories: ["Featured", "All", "Schools", "Higher Ed", "EdTech", "Corporate Training", "Tutoring", "Academies"],
    hero: {
      headline: "Turn your students, programs, and outcomes into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with student and program screens, progress agents, enrollment Decisions, reporting Receipts, and a growth path shaped around your institution.",
    },
  },
  "professional-services": {
    label: "Professional Services",
    categories: ["Featured", "All", "Consulting", "Recruiting", "HR", "Accounting", "Design", "Agencies"],
    hero: {
      headline: "Turn your clients, projects, and delivery into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with client and project screens, resourcing agents, scope Decisions, billable Receipts, and a growth path shaped around your practice.",
    },
  },
  nonprofit: {
    label: "Nonprofit",
    categories: ["Featured", "All", "Charities", "Foundations", "NGOs", "Faith", "Community", "Advocacy"],
    hero: {
      headline: "Turn your donors, programs, and impact into an AI Agentic Application.",
      subcopy: "Answer a few tight questions, and the Architect builds a working app with donor and program screens, outreach agents, grant Decisions, impact Receipts, and a growth path shaped around your mission.",
    },
  },
} satisfies Record<string, Vertical>;

export type VerticalKey = keyof typeof VERTICALS;
